import { Can } from '@/domains/access-control';
import { useJobTitles } from '@/domains/users/hooks/useUsers';
import { apiClient } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import { ConfirmDialog } from '@/shared/components/ConfirmDialog';
import { PageHeader } from '@/shared/components/PageHeader';
import { showErrorToast, showSuccessToast } from '@/shared/notifications/toast-utils';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Loader2, Plus, Trash2, Users } from 'lucide-react';
import { useState } from 'react';

const JOB_TITLES_KEY = [...queryKeys.users.all, 'jobTitles'];

import {
  ALLOWED_TEXT_PATTERN as ALLOWED_PATTERN,
  TEXT_VALIDATION_MSG as VALIDATION_MSG,
} from '@/shared/lib/validation';

export function JobTitlesPage() {
  const { data: jobTitlesData, isLoading } = useJobTitles();
  const queryClient = useQueryClient();
  const [newTitle, setNewTitle] = useState('');
  const [error, setError] = useState('');
  // Job title pending deletion — drives the confirmation dialog (null = closed).
  const [titleToDelete, setTitleToDelete] = useState<{ id: number; name: string } | null>(null);

  const createMutation = useMutation({
    mutationFn: (name: string) =>
      apiClient.post<{ id: number; name: string }>('/works-lite/users/lookups/job-titles', {
        name,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: JOB_TITLES_KEY });
      setNewTitle('');
      showSuccessToast('Job Title Created', 'The job title has been added successfully.');
    },
    onError: (err: any) => {
      showErrorToast('Failed to Create', err?.message || 'An unexpected error occurred.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      apiClient.delete<{ success: boolean }>(`/works-lite/users/lookups/job-titles/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: JOB_TITLES_KEY });
      setTitleToDelete(null);
      showSuccessToast('Job Title Removed', 'The job title has been removed.');
    },
    onError: (err: any) => {
      showErrorToast('Failed to Remove', err?.message || 'An unexpected error occurred.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newTitle.trim();
    if (!trimmed) return;
    if (trimmed.length > 50) {
      setError('Job title must be 50 characters or less');
      return;
    }
    if (!ALLOWED_PATTERN.test(trimmed)) {
      setError(VALIDATION_MSG);
      return;
    }
    setError('');
    createMutation.mutate(trimmed);
  };

  const jobTitles = jobTitlesData?.values ?? [];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <PageHeader
        title="Job Titles"
        subtitle="Create and manage job titles for user profiles"
        icon={<Users className="w-7 h-7" style={{ color: '#E84A2B' }} />}
      />

      <div className="flex-1 overflow-y-auto px-6 pb-6">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Add New */}
          <Can action="create" subject="JobTitle">
            <div className="bg-card border border-border rounded-lg p-5">
              <h3 className="text-sm font-semibold text-foreground mb-3">Add New Job Title</h3>
              <form onSubmit={handleSubmit} className="flex items-start gap-3">
                <div className="flex-1">
                  <Input
                    value={newTitle}
                    onChange={(e) => {
                      setNewTitle(e.target.value);
                      if (error) setError('');
                    }}
                    placeholder="e.g. Field Engineer"
                    maxLength={50}
                    className="bg-background border-border"
                  />
                  {error && (
                    <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {error}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={!newTitle.trim() || createMutation.isPending}
                  className="bg-[#E84A2B] hover:bg-[#d43d20] text-white"
                >
                  {createMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-1" />
                  )}
                  Add
                </Button>
              </form>
            </div>
          </Can>

          {/* List */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-border bg-muted/30">
              <h3 className="text-sm font-semibold text-foreground">
                Existing Job Titles ({jobTitles.length})
              </h3>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : jobTitles.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground text-sm">
                No job titles configured yet
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {jobTitles.map((jt: { id: number; name: string }) => (
                  <li
                    key={jt.id}
                    className="flex items-center justify-between px-5 py-3 hover:bg-accent/50 transition-colors"
                  >
                    <span className="text-sm text-foreground">{jt.name}</span>
                    <Can action="delete" subject="JobTitle">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setTitleToDelete(jt)}
                        disabled={deleteMutation.isPending}
                        className="text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </Can>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={titleToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setTitleToDelete(null);
        }}
        title="Delete Job Title"
        description={
          titleToDelete
            ? `"${titleToDelete.name}" will be removed. This action cannot be undone.`
            : undefined
        }
        confirmLabel="Delete"
        pendingLabel="Removing…"
        isPending={deleteMutation.isPending}
        onConfirm={() => {
          if (titleToDelete) deleteMutation.mutate(titleToDelete.id);
        }}
      />
    </div>
  );
}
